import { API, Auth } from 'aws-amplify';
import React, { FC, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { useParams } from 'react-router';
import { RootState, useDispatch, useSelector } from 'src/store';
import { pagesReset } from 'src/store/modules/pages';
import modalSlickAlt from 'src/images/modalSlickAlt.png';

import {
  editTextReset,
  editColor,
  editFontSize,
  editFontWeight,
  editPreFontSize,
  editText
} from 'src/store/modules/editText';
import {
  buttonsReset,
  editBtnBgColor,
  editBtnColor,
  editBtnFontSize,
  editBtnFontWeight,
  editBtnPreFontSize,
  editBtnText
} from 'src/store/modules/buttons';
import {
  imageReset,
  editBgImage,
  editCurrentIndex,
  editDeeplink
} from 'src/store/modules/imageUpload';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import setInitValue from 'src/utils/setInitValue';
import { changeText } from 'src/utils/changeText';
import { changeEnter } from 'src/utils/changeEnter';
import { editUserCompany } from 'src/store/modules/client';
import { trackingReset } from 'src/store/modules/trackings';
import { getFontURL } from 'src/utils/getFontUrl';
import { initTrackingValues } from '../TemplateTracking/SimpleModalSlideTracking';

const initViewValues = {
  currentIndex: 0,
  bg: 'color',
  bgImage: {
    background: '',
    multi_image: [
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp',
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp',
      'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp'
    ]
  },
  bgColor: { background: '#ffffff' },
  deeplink: {
    multi_image: [
      'https://www.inappmessage.com/',
      'https://www.inappmessage.com/',
      'https://www.inappmessage.com/'
    ]
  },
  radius: { multi_image: 16, multi_left_button: 8, multi_right_button: 8 },

  text: {
    multi_title: ['Title 1', 'Title 2', 'Title 3'],
    multi_sub_title: ['Subtitle 1', 'Subtitle 2', 'Subtitle 3'],
    multi_box_title: ['Box Title 1', 'Box Title 2', 'Box Title 3']
  },
  fontSize: { multi_title: 1.25, multi_sub_title: 0.75, multi_box_title: 1 },
  fontWeight: { multi_title: 700, multi_sub_title: 400, multi_box_title: 700 },
  color: {
    multi_title: '#ffffff',
    multi_sub_title: '#ffffff',
    multi_box_title: '#000000'
  },
  fontFamily: {
    multi_title: 'Montserrat',
    multi_sub_title: 'Montserrat',
    multi_box_title: 'Montserrat'
  },
  textAlign: {
    multi_title: 'left',
    multi_sub_title: 'left',
    multi_box_title: 'center'
  },

  btnText: {
    multi_left_button: ['Close', 'Close', 'Close'],
    multi_right_button: ['See more', 'See more', 'See more']
  },
  btnFontSize: { multi_left_button: 0.75, multi_right_button: 0.75 },
  btnFontWeight: { multi_left_button: 500, multi_right_button: 500 },
  btnColor: { multi_left_button: '#828282', multi_right_button: '#ffffff' },
  btnBgColorR: { multi_left_button: 254, multi_right_button: 0 },
  btnBgColorG: { multi_left_button: 254, multi_right_button: 69 },
  btnBgColorB: { multi_left_button: 254, multi_right_button: 69 },
  btnBgColorA: { multi_left_button: 1, multi_right_button: 1 },
  btnFontFamily: {
    multi_left_button: 'Montserrat',
    multi_right_button: 'Montserrat'
  }
};

const SimpleModalSlide: FC = () => {
  const dispatch = useDispatch();
  const sliderRef = useRef<any>(null);
  const contentsSliderRef = useRef<any>(null);

  useEffect(() => {
    async function getUserId() {
      try {
        const current = await Auth.currentUserInfo();

        dispatch(editUserCompany(current.attributes['custom:Company']));
      } catch (error) {
        console.log('error');
      }
    }
    getUserId();
  }, []);

  const clientName = useSelector(
    (state: RootState) => state.client.userCompany
  );
  const currentIndex = useSelector(
    (state: RootState) => state.imageUpload.currentIndex
  );
  const bg = useSelector((state: RootState) => state.imageUpload.bg);
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const multiTitleFontURL = getFontURL(fontFamily?.multi_title);
  const multiSubTitleFontURL = getFontURL(fontFamily?.multi_sub_title);
  const multiBoxTitleFontURL = getFontURL(fontFamily?.multi_box_title);
  const multiLeftButtonFontURL = getFontURL(btnFontFamily?.multi_left_button);
  const multiRightButtonFontURL = getFontURL(btnFontFamily?.multi_right_button);

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          if (
            customValues?.bgImage &&
            customValues?.text &&
            customValues?.btnText
          ) {
            setInitValue(dispatch, customValues);
          } else {
            setValue(
              dispatch,
              customValues,
              { ...initViewValues, ...initTrackingValues },
              clientName
            );
          }
        }
      } catch (error) {}
    }
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });
    onLoad();
    return () => {
      dispatch(buttonsReset());

      dispatch(editTextReset());
      dispatch(imageReset());
      dispatch(pagesReset());
      dispatch(trackingReset());
    };
  }, []);

  const style = `
  @Font-face {
    font-family: 'multiTitle';
    src: url(${multiTitleFontURL});
  }
  @Font-face {
    font-family: 'multiSubTitle';
    src: url(${multiSubTitleFontURL});
  }
  @Font-face {
    font-family: 'multiBoxTitle';
    src: url(${multiBoxTitleFontURL});
  }
  @Font-face {
    font-family: 'multiLeftButton';
    src: url(${multiLeftButtonFontURL});
  }
  @Font-face {
    font-family: 'multiRightButton';
    src: url(${multiRightButtonFontURL});
  }
  .root_view {
    width: 100%;
    height: 100%;

    overflow: hidden;
    position: relative;
    background: none;
    display: flex;
    justify-content: center;
    
    .background {
      width: 100%;
      height: 100%;

      position: absolute;
      top: 0;
      background: rgba(0, 0, 0, 0.8);
    }
    .modal_wrapper {
      width: calc(100% - 76px);  

      display: flex;
      flex-direction: column;
      gap: 1rem;

      position: absolute;
      top: 50%;
      transform: translate(0%, -50%);
      
      overflow: hidden;
      border-radius: ${radius?.multi_image}px;
      ${
        bg === 'image'
          ? `background-image: url(${bgImage?.background});`
          : `
          background: linear-gradient(to top, ${bgColor?.background} 11.25rem, transparent 0);
          `
      };

      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    }

    .slick_wrapper,
    .slick_wrapper .slick-slider,
    .slick_wrapper .slick-list,
    .slick_wrapper .slick-track div,
    .slick_wrapper .slick-track img {
      width: 100%;
      aspect-ratio: 1;
      outline: none;
    }
    .slick_wrapper .slick-track img {
      object-fit: cover;
    }

    .slick_wrapper .image_box {
      width: 100%;
      height: 100%;
      position: relative;
    }
    .image_box .title_box {
      width: 100%;
      height: 5.875rem;
      padding: 1rem;

      position: absolute;
      bottom: 0;
      z-index: 1;
    }
    .image_box .title {
      width: 100%;
      height: 1.5rem;
      margin-bottom: 0.5rem;
      
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    
      text-align: ${textAlign?.multi_title};
      color: ${color?.multi_title};
      font-size: ${fontSize?.multi_title}rem;
      font-weight: ${fontWeight?.multi_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.multi_title
        )
          ? fontFamily?.multi_title
          : 'multiTitle'
      };
    }
    .image_box .sub_title {
      width: 100%;
      height: 1.75rem;

      display: flex;
      align-items: center;
    
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;

      text-align: ${textAlign?.multi_sub_title};
      color: ${color?.multi_sub_title};
      font-size: ${fontSize?.multi_sub_title}rem;
      font-weight: ${fontWeight?.multi_sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.multi_sub_title
        )
          ? fontFamily?.multi_sub_title
          : 'multiSubTitle'
      };
    }

    .contents_wrapper,
    .contents_box .title {
      height: 4.5rem;
    }
    .contents_box {
      height: 4.5rem;
      padding: 0 1.5rem;
    }
    .contents_box .title {
      width: 100%;
      height: 4.5rem;
      display: flex;
      align-items: center;
    }
    .contents_box .title .title_inner {
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;

      text-align: ${textAlign?.multi_box_title};
      color: ${color?.multi_box_title};
      font-size: ${fontSize?.multi_box_title}rem;
      font-weight: ${fontWeight?.multi_box_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.multi_box_title
        )
          ? fontFamily?.multi_box_title
          : 'multiBoxTitle'
      };
    }

    .dot_wrapper {
      margin-top: 1rem;
    }
    .dot_wrapper,
    .dot_wrapper li {
      height: 0.25rem;      
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      list-style-type: none;
    }
    .dot_wrapper button {
      width: 0.25rem;
      height: 0.25rem;

      font-size: 0;
      border: 0;
      outline: 0;
      border-radius: 999px;
      background-color: rgba(0, 0, 0, .3);
    }
    .dot_wrapper li.slick-active button {
      background-color: #000;
    }

    .bar_blank {
      width: 100%;
      height: 0.25rem;
    }
    .btn_wrapper {
      width: 100%;
      height: 2.5rem;
      padding: 0 1rem;
      margin-bottom: 1rem;

      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .btn_wrapper button {
      border: 0;
      background-color: transparent;
    }
    .btn_wrapper .style01 {
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      
      font-size: ${btnFontSize?.multi_left_button}rem;
      font-weight: ${btnFontWeight?.multi_left_button};
      color: ${btnColor?.multi_left_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.multi_left_button
        )
          ? btnFontFamily?.multi_left_button
          : 'multiLeftButton'
      };

      border-radius: ${radius?.multi_left_button}px;
      background-color:rgba(${btnBgColorR?.multi_left_button},${
    btnBgColorG?.multi_left_button
  }, ${btnBgColorB?.multi_left_button}, ${btnBgColorA?.multi_left_button});
    }
    .btn_wrapper .style02 {
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      font-size: ${btnFontSize?.multi_right_button}rem;
      font-weight: ${btnFontWeight?.multi_right_button};
      color: ${btnColor?.multi_right_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.multi_right_button
        )
          ? btnFontFamily?.multi_right_button
          : 'multiRightButton'
      };

      border-radius: ${radius?.multi_right_button}px;
      background-color:rgba(${btnBgColorR?.multi_right_button},${
    btnBgColorG?.multi_right_button
  }, ${btnBgColorB?.multi_right_button}, ${btnBgColorA?.multi_right_button});
    }   
  }
  `;

  useEffect(() => {
    sliderRef.current.slickGoTo(currentIndex);
    contentsSliderRef.current.slickGoTo(currentIndex);
  }, [currentIndex]);

  const settings = {
    arrows: false,
    dots: false,

    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 300,
    touchThreshold: 300
  };

  const contentsSettings = {
    arrows: false,
    dots: true,
    fade: true,

    dotsClass: 'dot_wrapper',
    appendDots: dots => <div id="id_dot_wrapper">{dots}</div>,
    speed: 300,
    swipe: false
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="root_view">
        <div className="background" />
        <div className="modal_wrapper">
          <div className="slick_wrapper">
            <Slider
              {...settings}
              ref={sliderRef}
              afterChange={index => {
                dispatch(editCurrentIndex(index));
                contentsSliderRef.current.slickGoTo(index);
              }}
            >
              {bgImage?.multi_image?.map((data: any, idx: number) => (
                <div key={idx} className="image_box">
                  <img
                    src={data && data !== '' ? data : modalSlickAlt}
                    alt=""
                  />
                  <div className="title_box">
                    <div className=" title">
                      {/* <div className="title_inner"> */}
                      {changeEnter(text?.multi_title?.[idx])}
                      {/* </div> */}
                    </div>
                    <div className="sub_title">
                      {/* <div className="sub_title_inner"> */}
                      {changeEnter(text?.multi_sub_title?.[idx])}
                      {/* </div> */}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <div className="contents_wrapper">
            <Slider {...contentsSettings} ref={contentsSliderRef}>
              {text?.multi_title?.map((data: any, idx: number) => (
                <div key={idx} className="contents_box">
                  <div className="title">
                    <div className="title_inner">
                      {changeEnter(text?.multi_box_title?.[idx])}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <div className="bar_blank" />
          <div className="btn_wrapper">
            <button className="style01">
              {btnText?.multi_left_button?.[currentIndex]}
            </button>
            <button className="style02">
              {btnText?.multi_right_button?.[currentIndex]}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const setValue = (
  dispatch: any,
  values: any,
  initViewValuess: any,
  clientName: any
) => {
  // console.log(values);
  // console.log('test');
  const TYPE = typeof values?.imageName;
  if (TYPE === 'string') {
    // dispatch(editBgColor(values?.bgColor));
    dispatch(
      editBgImage({
        multi_image: [
          `https://image.inappmessage.com/${clientName}/${values?.imageName}`
        ]
      })
    );
    //  dispatch(editBgImageBlur(values?.bgImageBlur));
    dispatch(editDeeplink({ multi_image: [values?.newDeepLink] }));
    //   dispatch(editRadius(values?.radius));
    dispatch(
      editText({
        multi_title: [''],
        multi_sub_title: [''],
        multi_box_title: [changeText(values?.newText)]
      })
    );

    dispatch(
      editBtnText({
        multi_left_button: [changeText(values?.newLeftBtnText)],
        multi_right_button: [changeText(values?.newRightBtnText)]
      })
    );
  } else {
    // dispatch(editBgColor(values?.bgColor));
    const MultiBackground = values?.imageName?.map(
      (data: any) => `https://image.inappmessage.com/${clientName}/${data}`
    );
    dispatch(
      editBgImage({
        multi_image: MultiBackground
      })
    );
    //  dispatch(editBgImageBlur(values?.bgImageBlur));
    dispatch(
      editDeeplink({
        multi_image: values?.newDeepLink
      })
    );
    //   dispatch(editRadius(values?.radius));

    const newText = values?.newText?.map((data: any) => changeText(data));
    dispatch(
      editText({
        multi_title: ['', '', ''],
        multi_sub_title: ['', '', ''],
        multi_box_title: newText
      })
    );

    dispatch(
      editBtnText({
        multi_left_button: [
          changeText(values?.newLeftBtnText?.[0]),
          changeText(values?.newLeftBtnText?.[1]),
          changeText(values?.newLeftBtnText?.[2])
        ],
        multi_right_button: [
          changeText(values?.newRightBtnText?.[0]),
          changeText(values?.newRightBtnText?.[1]),
          changeText(values?.newRightBtnText?.[2])
        ]
      })
    );
  }

  dispatch(
    editPreFontSize({
      ...initViewValuess?.fontSize,
      ...{
        multi_box_title: values?.newFontSize / 16
      }
    })
  );
  dispatch(
    editFontSize({
      ...initViewValuess?.fontSize,
      ...{
        multi_box_title: values?.newFontSize / 16
      }
    })
  );
  dispatch(
    editFontWeight({
      ...initViewValuess?.fontWeight,
      ...{
        multi_box_title: values?.newFontWeight
      }
    })
  );
  dispatch(
    editColor({
      ...initViewValuess?.color,
      ...{
        multi_box_title: values?.newTextColor
      }
    })
  );
  // dispatch(editFontFamily(values?.fontFamily));
  // dispatch(editTextAlign(values?.textAlign));

  dispatch(
    editBtnPreFontSize({
      multi_left_button: values?.newLeftBtnFontSize / 16,
      multi_right_button: values?.newRightBtnFontSize / 16
    })
  );
  dispatch(
    editBtnFontSize({
      multi_left_button: values?.newLeftBtnFontSize / 16,
      multi_right_button: values?.newRightBtnFontSize / 16
    })
  );
  dispatch(
    editBtnFontWeight({
      multi_left_button: values?.newLeftBtnFontWeight,
      multi_right_button: values?.newRightBtnFontWeight
    })
  );
  dispatch(
    editBtnColor({
      multi_left_button: values?.newLeftBtnTextColor,
      multi_right_button: values?.newRightBtnTextColor
    })
  );
  dispatch(
    editBtnBgColor(
      {
        multi_left_button: values?.RightBtnColorR,
        multi_right_button: values?.RightBtnColorR
      },
      {
        multi_left_button: values?.RightBtnColorG,
        multi_right_button: values?.RightBtnColorG
      },
      {
        multi_left_button: values?.RightBtnColorB,
        multi_right_button: values?.RightBtnColorB
      },
      {
        multi_left_button: 0,
        multi_right_button: values?.RightBtnColorA
      }
    )
  );
  //     dispatch(editBtnFontFamily(values?.btnFontFamily));
  //     dispatch(editBtnTextAlign(values?.btnTextAlign));
};

export default SimpleModalSlide;
